import { Badge } from "@chakra-ui/react";
import { DistributionStats } from "features/distributions/DistributionStats";
import { WaterfallEditMenu } from "features/waterfall/review/WaterfallEditMenu";
import {
  useFundFamilyEventRetrieveSuspense,
  useWaterfallBreakdownRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useGlobalEntityRetrieve } from "gen/endpoints/global-entity/global-entity";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { BooleanToggleButtonGroupValue } from "shared/components/button/ToggleButton";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  usePageTabs,
  UsePageTabsResult,
} from "shared/components/layout/PageTabs";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useCurrentPageTabId } from "shared/navigation/useCurrentPageTabId";

export interface WaterfallReviewPageContext {
  pageTabs: UsePageTabsResult;
  isEqualizedView: BooleanToggleButtonGroupValue;
  waterfallAdjustmentState: [
    boolean | null,
    Dispatch<SetStateAction<boolean | null>>,
  ];
}

export const WaterfallReviewPage: React.FC = () => {
  const { t } = useTranslation(["common", "waterfall"]);
  const currentPageTabId = useCurrentPageTabId();

  const {
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
    fundEntityId,
  } = useRequiredStringParams();

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data } = useWaterfallBreakdownRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
    fundEntityId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
  );
  const { data: fundFamilyEventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: entityData } = useGlobalEntityRetrieve(data.fund_id, {
    query: { enabled: !!data.fund_id },
  });

  const pageTabs = usePageTabs({
    hiddenTabs: {
      reviewInvestmentClassifications: !data.waterfall_is_hybrid,
    },
  });

  const waterfallAdjustmentState = useState<boolean | null>(false);
  const setAdjustmentModalIsOpen = waterfallAdjustmentState[1];

  const context: WaterfallReviewPageContext = {
    pageTabs,
    isEqualizedView,
    waterfallAdjustmentState,
  };

  return (
    <PageContent>
      <PageHeader
        title={t("waterfall:review.title")}
        backButton={{
          title: t("common:capitalEvent"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId }).$,
        }}
        badge={<Badge bg="gray.100">{entityData?.legal_name}</Badge>}
        stats={
          <DistributionStats
            data={data}
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedView}
            onEditAdjustmentsClick={() => setAdjustmentModalIsOpen(true)}
            showAdjustmentsButton={
              fundFamilyEventData.state === "in_progress" ||
              fundFamilyEventData.state === "draft"
            }
          />
        }
        actions={
          <WaterfallEditMenu
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            fundFamilySubEventId={fundFamilySubEventId}
            fundEntityId={fundEntityId}
            runWaterfall={true}
            isEqualizedView={isEqualizedView}
          />
        }
      />
      <PageBody key={currentPageTabId}>
        <QueryBoundary>
          <Outlet context={context} />
        </QueryBoundary>
      </PageBody>
    </PageContent>
  );
};
